/**
 * The cold-only day gate: a day whose source relations the archive lifecycle
 * has deleted, answered from the cold revision alone.
 *
 * Why this gate exists
 * --------------------
 * `cold.read_clearance` admits a day by re-reading the *live* source relation
 * and comparing it with the export. That is the right test while the source is
 * still there: it is what makes "the copy equals the table" a checked claim.
 * Once the delete stage has finished with a day, the source relation it would
 * read back is empty by design, so every relation of that day is red on its
 * own (`source_digest_mismatch`, the shape of a deleted source), and the
 * clearance can never go green again — while the cold revision of the day was
 * audited and verified when the source was still whole.
 *
 * For such a day the durable answer is the cold revision, so the read line
 * proves *that* instead of asking a clearance it can no longer earn. The proof
 * is the revision's own completeness plus the lifecycle's own record that the
 * source is gone:
 *
 *   - the day is source-gone by {@link usageSourceGonePredicate} (one rule,
 *     shared with hist closure, the hourly gate, list counts, read-clearance's
 *     `source_deleted` rename, and the deploy proof): the warm archive ledger
 *     is `deleted` with `deleted_at` set and
 *     `deleted_rows = source_rows = archived_rows`, or the cold hot-delete
 *     ledger is `deleted` with `deleted_rows` equal to the current base
 *     revision's row count. A half-deleted day (`deleting`, or a row count
 *     that does not match) never passes. The day's Shanghai `usage_logs`
 *     window must also be empty: a row that landed after the delete keeps the
 *     day out of the proof (it is the late sweep's, not a second delete's),
 *     which is the same `NOT EXISTS` probe the SQL predicate runs;
 *   - the base and the served view revisions are legal full-row manifests:
 *     coverage `complete` with no gap, or the archived H0 shape whose only
 *     gaps are the sixteen retired scalars (`usageLogsH0RetiredGapsOnly`, the
 *     same rule `coldLineKind` applies);
 *   - their row counts agree, and they equal the archive day's
 *     `archived_rows` (a revision that does not hold the whole day is not
 *     served);
 *   - the revision actually covers the relation the day's durable answer comes
 *     from (`usage_logs_archive` is one of the base revision's sources);
 *   - the revisions are the current ones: the view was built from the base
 *     that is current now (`view.source_rev`), and `cold.partitions` still
 *     names both (`current_rev`). A base that moved underneath the served view
 *     is exactly the stale-clearance window, so the day stops here.
 *   - a content anchor still holds for those current revisions. The latest
 *     `object_recheck` / `full_recheck` of the base and of the view is not
 *     red. The anchor's authoritative relation is the one that still holds
 *     the source (`coldOnlyDayAnchored`). On `deleted` or `deleting` that is
 *     `usage_logs_archive` alone: a `usage_logs` read-back is neither an ok
 *     nor a red, because the hot rows were removed on purpose. Every other
 *     day merges both relations into one timeline. The latest conclusion on
 *     that timeline must be a role-complete ok. A later content red on an
 *     authoritative relation removes it. `unit_failed`, `unit_missing`,
 *     `unit_setup_failed`, `unit_version_skew`, and a red whose
 *     `incomplete_roles` is non-empty do not. A source mismatch is not
 *     itself an anchor. `digest_src` is not an anchor.
 *
 * Fail closed is literal: no archive row, no revision, an unreadable registry,
 * any missing fact — the day is not admitted, and the caller leaves it to the
 * layer that still holds it (the hot table, or the warm archive for a deleted
 * day). Nothing is guessed from a partial proof.
 *
 * Consumers: the gateway's `usage_org_hourly` cold serving and the platform's
 * exact cold list count (`clearedColdDays`), and the platform's read line
 * (`inspectUsageDays` -> `judgeColdLineDay`), which additionally pins the
 * admitted revision to the revision the reader actually served.
 *
 * This module lives in contracts, which both images already copy. It imports
 * one predicate from the same package and nothing else.
 */
import { USAGE_LOGS_H0_RETIRED_FIELD_IDS, usageLogsH0RetiredGapsOnly } from './history-read-cold-line.ts';

/** The view every usage reader serves (line A). The cold-only proof is decided on it. */
export const COLD_USAGE_READ_VIEW = 'primary@2';

/**
 * Days whose archive status is `deleted` or `deleting`. `$1` day names.
 * The failure path of the hour gate uses this instead of the facts statement:
 * those days are refused, and every other day keeps its clearance.
 */
export const COLD_ONLY_ARCHIVE_STATUS_SQL = `SELECT archived_on::text AS day, status
  FROM usage_logs_archive_days
 WHERE archived_on = ANY($1::date[])
   AND status IN ('deleted', 'deleting')`;

/**
 * Which lifecycle ledger retired a day, and whether its row accounting closes.
 * `$1` day names. The cold-ledger branch is only used when
 * `cold.usage_hot_delete_days` exists; `usageSourceDays` probes first and falls
 * back to the archive-only statement, so a table that has not been migrated
 * yet is "no cold-ledger fact", never an error.
 */
export const USAGE_SOURCE_DAY_SQL = `SELECT w.day::text AS day,
       a.status AS archive_status,
       (pg_catalog.to_jsonb(a) ->> 'source_rows') AS archive_source_rows,
       (pg_catalog.to_jsonb(a) ->> 'archived_rows') AS archive_archived_rows,
       (pg_catalog.to_jsonb(a) ->> 'deleted_rows') AS archive_deleted_rows,
       h.status AS ledger_status,
       h.source_rows::text AS ledger_source_rows,
       h.deleted_rows::text AS ledger_deleted_rows,
       (h.delete_cursor_created_at IS NOT NULL) AS ledger_cursor_set
  FROM (SELECT DISTINCT pg_catalog.unnest($1::text[])::date AS day) w
  LEFT JOIN usage_logs_archive_days a ON a.archived_on = w.day
  LEFT JOIN cold.usage_hot_delete_days h ON h.day = w.day`;

export const USAGE_SOURCE_DAY_NO_LEDGER_SQL = `SELECT w.day::text AS day,
       a.status AS archive_status,
       (pg_catalog.to_jsonb(a) ->> 'source_rows') AS archive_source_rows,
       (pg_catalog.to_jsonb(a) ->> 'archived_rows') AS archive_archived_rows,
       (pg_catalog.to_jsonb(a) ->> 'deleted_rows') AS archive_deleted_rows,
       NULL::text AS ledger_status,
       NULL::text AS ledger_source_rows,
       NULL::text AS ledger_deleted_rows,
       FALSE AS ledger_cursor_set
  FROM (SELECT DISTINCT pg_catalog.unnest($1::text[])::date AS day) w
  LEFT JOIN usage_logs_archive_days a ON a.archived_on = w.day`;

export const USAGE_SOURCE_DAY_LEDGER_SQL = `SELECT w.day::text AS day,
       NULL::text AS archive_status,
       NULL::text AS archive_source_rows,
       NULL::text AS archive_archived_rows,
       NULL::text AS archive_deleted_rows,
       h.status AS ledger_status,
       h.source_rows::text AS ledger_source_rows,
       h.deleted_rows::text AS ledger_deleted_rows,
       (h.delete_cursor_created_at IS NOT NULL) AS ledger_cursor_set
  FROM (SELECT DISTINCT pg_catalog.unnest($1::text[])::date AS day) w
  LEFT JOIN cold.usage_hot_delete_days h ON h.day = w.day`;

export const USAGE_SOURCE_DAY_ARCHIVE_PROBE_SQL = `SELECT pg_catalog.to_regclass('usage_logs_archive_days')::text AS rel`;

export const USAGE_SOURCE_DAY_PROBE_SQL = `SELECT pg_catalog.to_regclass('cold.usage_hot_delete_days')::text AS rel`;

/**
 * `origin/main` text of {@link USAGE_SOURCE_DAY_SQL}. Sent whenever
 * `COLD_HOT_DELETE_EVIDENCE` is not `cold`, so the default read is byte-identical.
 */
export const USAGE_SOURCE_DAY_SQL_MAIN = `SELECT w.day::text AS day,
       a.status AS archive_status,
       (pg_catalog.to_jsonb(a) ->> 'source_rows') AS archive_source_rows,
       (pg_catalog.to_jsonb(a) ->> 'archived_rows') AS archive_archived_rows,
       (pg_catalog.to_jsonb(a) ->> 'deleted_rows') AS archive_deleted_rows,
       h.status AS ledger_status,
       h.source_rows::text AS ledger_source_rows,
       h.deleted_rows::text AS ledger_deleted_rows
  FROM (SELECT DISTINCT pg_catalog.unnest($1::text[])::date AS day) w
  LEFT JOIN usage_logs_archive_days a ON a.archived_on = w.day
  LEFT JOIN cold.usage_hot_delete_days h ON h.day = w.day`;

export const USAGE_SOURCE_DAY_NO_LEDGER_SQL_MAIN = `SELECT w.day::text AS day,
       a.status AS archive_status,
       (pg_catalog.to_jsonb(a) ->> 'source_rows') AS archive_source_rows,
       (pg_catalog.to_jsonb(a) ->> 'archived_rows') AS archive_archived_rows,
       (pg_catalog.to_jsonb(a) ->> 'deleted_rows') AS archive_deleted_rows,
       NULL::text AS ledger_status,
       NULL::text AS ledger_source_rows,
       NULL::text AS ledger_deleted_rows
  FROM (SELECT DISTINCT pg_catalog.unnest($1::text[])::date AS day) w
  LEFT JOIN usage_logs_archive_days a ON a.archived_on = w.day`;

export const USAGE_SOURCE_DAY_LEDGER_SQL_MAIN = `SELECT w.day::text AS day,
       NULL::text AS archive_status,
       NULL::text AS archive_source_rows,
       NULL::text AS archive_archived_rows,
       NULL::text AS archive_deleted_rows,
       h.status AS ledger_status,
       h.source_rows::text AS ledger_source_rows,
       h.deleted_rows::text AS ledger_deleted_rows
  FROM (SELECT DISTINCT pg_catalog.unnest($1::text[])::date AS day) w
  LEFT JOIN cold.usage_hot_delete_days h ON h.day = w.day`;

/**
 * Which statement a reader sends. `main` is the default lane (evidence not
 * `cold`): the pre-change text, and a `deleting` ledger row is terminal the
 * way main was. `cold-evidence` adds the cursor boolean and treats a
 * `deleting` row as a source only after a row has actually been removed, so
 * the grace (nothing deleted yet) stays on the hot table.
 */
export type UsageSourceReadMode = 'main' | 'cold-evidence';

/**
 * A `deleting` ledger row is visible to readers only after a row has been
 * removed or the delete cursor has been set. The cursor column is also the
 * barrier mark committed in its own transaction before the first DELETE, so
 * a hold at `deleted_rows = 0` with a null cursor has not started. Both facts
 * come from {@link USAGE_SOURCE_DAY_SQL}; there is no follow-up probe.
 *
 * Cold-evidence reads ignore the barrier timestamp: `deleted_rows = 0` is
 * still the full hot table, cursor or not. Only a removed row switches the
 * reader onto the cold prefix.
 */
export function coldLedgerDeleteVisible(
  ledgerStatus: string | null,
  deletedRows: number | null,
  cursorSet: boolean,
): boolean {
  if (ledgerStatus === 'deleted') return true;
  if (ledgerStatus !== 'deleting') return false;
  return (deletedRows ?? 0) > 0 || cursorSet;
}

/** Retired scalar ids as a SQL integer list, for the H0-only partial manifest. */
const H0_IDS_SQL = USAGE_LOGS_H0_RETIRED_FIELD_IDS.join(', ');

/** The legal full-row manifest of the cold line: complete, or H0-only partial. */
function legalRevisionSql(alias: string): string {
  return `((${alias}.coverage = 'complete' AND pg_catalog.jsonb_array_length(${alias}.gaps) = 0)
        OR (${alias}.coverage = 'partial'
            AND pg_catalog.jsonb_array_length(${alias}.gaps) > 0
            AND EXISTS (SELECT 1 FROM pg_catalog.jsonb_array_elements(${alias}.gaps) gap
                         WHERE pg_catalog.jsonb_array_length(COALESCE(gap -> 'fields', '[]'::jsonb)) > 0)
            AND NOT EXISTS (
              SELECT 1
                FROM pg_catalog.jsonb_array_elements(${alias}.gaps) gap
                CROSS JOIN LATERAL pg_catalog.jsonb_array_elements_text(COALESCE(gap -> 'fields', '[]'::jsonb)) field
               WHERE field::int NOT IN (${H0_IDS_SQL}))))`;
}

function publishedUnretractedSql(alias: string, layer: 'base' | 'view', viewSql: string): string {
  const viewMatch = layer === 'base' ? "e.view = ''" : `e.view = ${viewSql}`;
  return `EXISTS (SELECT 1 FROM cold.revision_events e
                   WHERE e.layer = '${layer}' AND e.dataset = 'usage.logs' AND ${viewMatch}
                     AND e.day = ${alias}.day AND e.rev = ${alias}.rev AND e.event = 'published')
      AND NOT EXISTS (SELECT 1 FROM cold.revision_events e
                       WHERE e.layer = '${layer}' AND e.dataset = 'usage.logs' AND ${viewMatch}
                         AND e.day = ${alias}.day AND e.rev = ${alias}.rev AND e.event = 'retracted')`;
}

/**
 * Boolean SQL for one day. `dayExpr` is a qualified date column (`g.day`).
 * `viewSql` is either `$2` or a quoted view name. `ledgerPresent` false omits
 * `cold.usage_hot_delete_days` entirely, for a database that has not migrated it.
 */
function sourceGonePredicate(dayExpr: string, viewSql: string, ledgerPresent: boolean, schema = ''): string {
  if (!/^(?:[a-z_][a-z0-9_]*\.)?$/u.test(schema)) {
    throw new Error(`sourceGonePredicate: schema must be empty or an identifier prefix, got ${schema}`);
  }
  const archive = `EXISTS (
        SELECT 1 FROM ${schema}usage_logs_archive_days a
         WHERE a.archived_on = br.day
           AND a.status = 'deleted'
           AND a.deleted_at IS NOT NULL
           AND (pg_catalog.to_jsonb(a) ->> 'deleted_rows')::bigint = (pg_catalog.to_jsonb(a) ->> 'source_rows')::bigint
           AND (pg_catalog.to_jsonb(a) ->> 'archived_rows')::bigint = (pg_catalog.to_jsonb(a) ->> 'source_rows')::bigint
           AND br.rows = (pg_catalog.to_jsonb(a) ->> 'source_rows')::bigint
           AND (br.src -> 'relations') @> '["usage_logs_archive"]'::jsonb)`;
  const ledger = `EXISTS (
        SELECT 1 FROM cold.usage_hot_delete_days h
         WHERE h.day = br.day
           AND h.status = 'deleted'
           AND h.deleted_rows = br.rows
           AND (br.src -> 'relations') @> '["usage_logs"]'::jsonb)`;
  const closed = ledgerPresent ? `(${archive} OR ${ledger})` : archive;
  return `EXISTS (
    SELECT 1
      FROM cold.partitions bp
      JOIN cold.revisions br
        ON br.layer = bp.layer AND br.dataset = bp.dataset AND br.view = ''
       AND br.day = bp.day AND br.rev = bp.current_rev
      JOIN cold.partitions vp
        ON vp.layer = 'view' AND vp.dataset = 'usage.logs' AND vp.view = ${viewSql}
       AND vp.day = bp.day
      JOIN cold.revisions vr
        ON vr.layer = vp.layer AND vr.dataset = vp.dataset AND vr.view = vp.view
       AND vr.day = vp.day AND vr.rev = vp.current_rev
     WHERE bp.layer = 'base' AND bp.dataset = 'usage.logs' AND bp.view = ''
       AND bp.day = ${dayExpr}
       AND ${publishedUnretractedSql('br', 'base', viewSql)}
       AND ${publishedUnretractedSql('vr', 'view', viewSql)}
       AND vr.rows = br.rows
       AND vr.source_rev = br.rev
       AND ${legalRevisionSql('br')}
       AND ${legalRevisionSql('vr')}
       AND ${closed}
       AND NOT EXISTS (
         SELECT 1 FROM ${schema}usage_logs hot_left
          WHERE hot_left.created_at >= (br.day::timestamp AT TIME ZONE 'Asia/Shanghai')
            AND hot_left.created_at < ((br.day + 1)::timestamp AT TIME ZONE 'Asia/Shanghai')
          ORDER BY hot_left.created_at
          LIMIT 1))`;
}

/**
 * The one source-gone rule, as a boolean expression for `dayExpr`.
 *
 * A day qualifies only when the current base and the current `primary@2`
 * view are both published, not retracted, and a legal revision (coverage
 * `complete` with no gaps, or `partial` whose only gaps are the retired H0
 * fields), the view was built from that base (`source_rev` and the row
 * counts match), and one ledger has closed:
 *
 * - warm `usage_logs_archive_days`: `deleted`, `deleted_at` set,
 *   `deleted_rows = source_rows = archived_rows`, base rows equal
 *   `source_rows`, and `src.relations` contains `usage_logs_archive`; or
 * - cold `usage_hot_delete_days`: `deleted`, `deleted_rows` equals the
 *   current base revision's row count, and `src.relations` contains
 *   `usage_logs`.
 *
 * A hot row still inside the Shanghai day is not source-gone: the probe is
 * one `ORDER BY created_at LIMIT 1` on `usage_logs`, so the planner uses the
 * `created_at` range index (or the day's partition of it). `deleting`, a row-count
 * mismatch, a missing ledger table, a retracted revision, or a cold gap is
 * not source-gone. `copying` is not source-gone
 * and does not, by itself, keep a day open. Hist closure, `usageSourceGoneDays`,
 * cold-only serving, the hourly gate, list counts, the read-clearance
 * `source_deleted` rename, and the deploy proof all use this text.
 * Cold-only serving adds the content anchor on top; it does not replace the rule.
 */
export function usageSourceGonePredicate(dayExpr: string, schema = ''): string {
  if (!/^[a-z_][a-z0-9_]*\.[a-z_][a-z0-9_]*$/u.test(dayExpr)) {
    throw new Error(`usageSourceGonePredicate: day expression must be a qualified column, got ${dayExpr}`);
  }
  return sourceGonePredicate(dayExpr, `'${COLD_USAGE_READ_VIEW}'`, true, schema);
}

function usageSourceGoneSql(ledgerPresent: boolean): string {
  return `WITH wanted AS MATERIALIZED (
  SELECT DISTINCT pg_catalog.unnest($1::text[])::date AS day
)
SELECT w.day::text AS day
  FROM wanted w
 WHERE ${sourceGonePredicate('w.day', '$2', ledgerPresent)}
 ORDER BY w.day`;
}

/**
 * The one source-gone statement. `$1` day names, `$2` the usage view.
 * Same predicate as {@link usageSourceGonePredicate}. Requires
 * `cold.usage_hot_delete_days`; `usageSourceGoneDays` probes and uses the
 * archive-only form when the migration is absent.
 */
export const USAGE_SOURCE_GONE_SQL = usageSourceGoneSql(true);

/** The same statement without the cold ledger; exported for the probe fallback and tests. */
export const USAGE_SOURCE_GONE_NO_LEDGER_SQL = usageSourceGoneSql(false);

/** The narrow executor the callers already have. */
export interface ColdOnlyQuery {
  query(sql: string, values?: readonly unknown[]): Promise<{ readonly rows: readonly Readonly<Record<string, unknown>>[] }>;
}

/** One revision the gate judges. */
export interface ColdOnlyRevision {
  readonly rev: number;
  readonly rows: number;
  readonly coverage: 'complete' | 'partial' | null;
  readonly gapFieldIds: readonly number[];
  /** `cold.revisions.source_rev`, the base revision a view was built from (null for a base). */
  readonly sourceRev: number | null;
  /** The base revision's `src.relations` names `usage_logs_archive`. */
  readonly hasArchiveRelation: boolean;
  /** The base revision's `src.relations` names `usage_logs`. */
  readonly hasHotRelation: boolean;
}

/** Which lifecycle ledger records that this day's source is gone. */
export type UsageSourceKind = 'archive' | 'cold_ledger';

/** One day's lifecycle status, from either the archive day table or the cold ledger. */
export interface UsageSourceDay {
  readonly day: string;
  readonly source: UsageSourceKind | null;
  readonly status: 'deleted' | 'deleting' | null;
  /** `deleted_rows` of the ledger that names the day. */
  readonly deletedRows: number | null;
  /** `source_rows` of the ledger that names the day (the cold ledger records it too). */
  readonly sourceRows: number | null;
  /** The ledger says `deleted` and its row accounting is present (revision rows checked by the caller). */
  readonly closed: boolean;
}

/**
 * Every fact the gate reads for one day. `null` is a missing fact, never a
 * default. All rows are parsed from text so the answer does not depend on the
 * pool's type parsers.
 */
export interface ColdOnlyDayFacts {
  readonly day: string;
  /** `usage_logs_archive_days.status`, null when the day is not an archive day at all. */
  readonly archiveStatus: string | null;
  readonly sourceRows: number | null;
  readonly archivedRows: number | null;
  readonly deletedRows: number | null;
  readonly deletedAtSet: boolean;
  readonly baseRevision: ColdOnlyRevision | null;
  readonly viewRevision: ColdOnlyRevision | null;
  readonly currentBaseRev: number | null;
  readonly currentViewRev: number | null;
  /** Latest `object_recheck` / `full_recheck` of the current base or view is red. Missing is not red. */
  readonly recheckRed: boolean;
  /**
   * Read-backs of the authoritative relation on the current view revision.
   * `deleted` / `deleting`: `usage_logs_archive` only. Otherwise both
   * relations, one timeline. The latest conclusion is a role-complete ok.
   * A later content red on an authoritative relation removes it.
   * `unit_failed`, `unit_missing`, `unit_setup_failed`, `unit_version_skew`,
   * and a red with `incomplete_roles` do not.
   */
  readonly contentAnchored: boolean;
  /** The lifecycle ledger that retired this day's source; null while the source is still expected. */
  readonly source: UsageSourceKind | null;
  /** That ledger records `deleted` with its row accounting. */
  readonly sourceClosed: boolean;
  /**
   * A `usage_logs` row still exists inside the day's Shanghai window. A late
   * row after the delete means the source is not gone: the same rule's SQL
   * form (`usageSourceGonePredicate`) says so, and a late row is the late
   * sweep's business, not another hot delete.
   */
  readonly hotPresent: boolean;
  /**
   * `sourceClosed` plus the cold revision proof: base and view are the current,
   * legal full-row revisions of this day, built from one another, and hold the
   * rows the ledger retired, with no hot row left. This is the one "source is
   * gone" answer every consumer uses.
   */
  readonly sourceGone: boolean;
}

/**
 * One statement for any number of days. For every requested day it returns the
 * archive day's own record, the current published base and view revisions and
 * the current revision of both partitions; a day it knows nothing about comes
 * back with nulls, so the caller can tell "not an archive day" from "an archive
 * day whose revision is gone". `$1` day names, `$2` the usage view name,
 * `$3`/`$4` the first and last of those days. The scans are date ranges, not
 * one index probe per day.
 *
 * The reads are one pass each (`MATERIALIZED`), not a lateral per day. A
 * read-back keeps only the fields the anchor reads: `ok`, `id`, `at`,
 * `incomplete_roles`, `problems_total`, `data_problems_total`, and each
 * problem's `code` (a string in the `problems` array). Expanding each problem
 * object inside the aggregate, or embedding the raw `problems` jsonb, both
 * take seconds on the production read; the jsonpath projection does not.
 * On a warm day whose archive ledger has closed `deleted` (row counts and
 * `deleted_at`), the hot read-backs are not loaded. A `deleting` day keeps
 * them, so a cold-ledger finish can still anchor on the read-back from
 * before the delete.
 *
 * Each day also carries `hot_present`: one `EXISTS ... ORDER BY created_at
 * LIMIT 1` probe on `usage_logs` over the Shanghai day, the same probe the
 * source-gone predicate runs. A row that landed after the delete keeps the day
 * out of the served proof; the platform already reads `usage_logs` on its hot
 * path, so this probe adds no new owner boundary (ADR-009 §1).
 */
export const COLD_ONLY_DAY_FACTS_SQL = `WITH wanted AS MATERIALIZED (
  SELECT DISTINCT unnest($1::text[])::date AS day
), rev_slice AS MATERIALIZED (
  SELECT layer, view, day, rev, rows, coverage, gaps, source_rev,
         (src -> 'relations') AS relations
    FROM cold.revisions
   WHERE dataset = 'usage.logs'
     AND day >= $3::date AND day <= $4::date
     AND ((layer = 'base' AND view = '') OR (layer = 'view' AND view = $2))
), base_events AS MATERIALIZED (
  SELECT day, rev,
         bool_or(event = 'published') AS published,
         bool_or(event = 'retracted') AS retracted
    FROM cold.revision_events
   WHERE layer = 'base' AND dataset = 'usage.logs' AND view = ''
     AND day >= $3::date AND day <= $4::date
     AND event IN ('published', 'retracted')
   GROUP BY day, rev
), view_events AS MATERIALIZED (
  SELECT day, rev,
         bool_or(event = 'published') AS published,
         bool_or(event = 'retracted') AS retracted
    FROM cold.revision_events
   WHERE layer = 'view' AND dataset = 'usage.logs' AND view = $2
     AND day >= $3::date AND day <= $4::date
     AND event IN ('published', 'retracted')
   GROUP BY day, rev
), base AS MATERIALIZED (
  SELECT DISTINCT ON (r.day) r.day, r.rev, r.rows, r.coverage, r.gaps, r.relations
    FROM rev_slice r
    JOIN base_events ev ON ev.day = r.day AND ev.rev = r.rev AND ev.published AND NOT ev.retracted
   WHERE r.layer = 'base' AND r.view = ''
   ORDER BY r.day, r.rev DESC
), vw AS MATERIALIZED (
  SELECT DISTINCT ON (v.day) v.day, v.rev, v.rows, v.coverage, v.gaps, v.source_rev
    FROM rev_slice v
    JOIN view_events ev ON ev.day = v.day AND ev.rev = v.rev AND ev.published AND NOT ev.retracted
   WHERE v.layer = 'view' AND v.view = $2
   ORDER BY v.day, v.rev DESC
), gone_days AS MATERIALIZED (
  SELECT archived_on AS day
    FROM usage_logs_archive_days
   WHERE archived_on >= $3::date AND archived_on <= $4::date
     AND status = 'deleted'
     AND deleted_at IS NOT NULL
     AND deleted_rows = source_rows
     AND archived_rows = source_rows
), read_back AS MATERIALIZED (
  SELECT au.day, au.rev, au.source_relation,
         jsonb_agg(jsonb_build_object(
           'ok', au.ok,
           'id', au.id::text,
           'at', to_char(au.at AT TIME ZONE 'UTC', 'YYYY-MM-DD"T"HH24:MI:SS.US"Z"'),
           'detail', jsonb_build_object(
             'incomplete_roles', au.detail -> 'incomplete_roles',
             'problems_total', au.detail -> 'problems_total',
             'data_problems_total', au.detail -> 'data_problems_total',
             'problems', jsonb_path_query_array(
               CASE WHEN jsonb_typeof(au.detail -> 'problems') = 'array'
                    THEN au.detail -> 'problems' ELSE '[]'::jsonb END,
               '$[*].code'
             )
           )
         ) ORDER BY au.at, au.id) AS audits
    FROM cold.verifications au
    LEFT JOIN gone_days g ON g.day = au.day
   WHERE au.kind = 'read_audit' AND au.layer = 'view' AND au.dataset = 'usage.logs'
     AND au.view = $2
     AND au.day >= $3::date AND au.day <= $4::date
     AND (au.source_relation IS DISTINCT FROM 'usage_logs' OR g.day IS NULL)
   GROUP BY au.day, au.rev, au.source_relation
), recheck_one AS MATERIALIZED (
  SELECT DISTINCT ON (chk.layer, chk.view, chk.day, chk.rev, chk.kind)
         chk.layer, chk.view, chk.day, chk.rev, NOT chk.ok AS red
    FROM cold.verifications chk
   WHERE chk.dataset = 'usage.logs'
     AND chk.kind IN ('object_recheck', 'full_recheck')
     AND chk.day >= $3::date AND chk.day <= $4::date
     AND ((chk.layer = 'base' AND chk.view = '') OR (chk.layer = 'view' AND chk.view = $2))
   ORDER BY chk.layer, chk.view, chk.day, chk.rev, chk.kind, chk.at DESC, chk.id DESC
), archive_slice AS MATERIALIZED (
  SELECT archived_on AS day, status, source_rows, archived_rows, deleted_rows, deleted_at
    FROM usage_logs_archive_days
   WHERE archived_on >= $3::date AND archived_on <= $4::date
), part_slice AS MATERIALIZED (
  SELECT layer, view, day, current_rev
    FROM cold.partitions
   WHERE dataset = 'usage.logs'
     AND day >= $3::date AND day <= $4::date
     AND ((layer = 'base' AND view = '') OR (layer = 'view' AND view = $2))
), recheck_red AS MATERIALIZED (
  SELECT layer, view, day, rev, bool_or(red) AS red
    FROM recheck_one
   GROUP BY layer, view, day, rev
)
SELECT w.day::text AS day,
       a.status AS archive_status,
       a.source_rows::text AS source_rows,
       a.archived_rows::text AS archived_rows,
       a.deleted_rows::text AS deleted_rows,
       (a.deleted_at IS NOT NULL) AS deleted_at_set,
       b.rev::text AS base_rev,
       b.rows::text AS base_rows,
       b.coverage AS base_coverage,
       b.gaps AS base_gaps,
       b.relations AS base_relations,
       v.rev::text AS view_rev,
       v.rows::text AS view_rows,
       v.coverage AS view_coverage,
       v.gaps AS view_gaps,
       v.source_rev::text AS view_source_rev,
       pb.current_rev::text AS current_base_rev,
       pv.current_rev::text AS current_view_rev,
       COALESCE(base_red.red, false) AS base_recheck_red,
       COALESCE(view_red.red, false) AS view_recheck_red,
       COALESCE(logs_audit.audits, '[]'::jsonb) AS logs_audits,
       COALESCE(arch_audit.audits, '[]'::jsonb) AS archive_audits,
       EXISTS (
         SELECT 1 FROM usage_logs hot_left
          WHERE hot_left.created_at >= (w.day::timestamp AT TIME ZONE 'Asia/Shanghai')
            AND hot_left.created_at < ((w.day + 1)::timestamp AT TIME ZONE 'Asia/Shanghai')
          ORDER BY hot_left.created_at
          LIMIT 1) AS hot_present
  FROM wanted w
  LEFT JOIN archive_slice a ON a.day = w.day
  LEFT JOIN base b ON b.day = w.day
  LEFT JOIN vw v ON v.day = w.day
  LEFT JOIN part_slice pb
    ON pb.layer = 'base' AND pb.view = '' AND pb.day = w.day
  LEFT JOIN part_slice pv
    ON pv.layer = 'view' AND pv.view = $2 AND pv.day = w.day
  LEFT JOIN read_back logs_audit
    ON logs_audit.day = w.day AND logs_audit.rev = v.rev AND logs_audit.source_relation = 'usage_logs'
  LEFT JOIN read_back arch_audit
    ON arch_audit.day = w.day AND arch_audit.rev = v.rev AND arch_audit.source_relation = 'usage_logs_archive'
  LEFT JOIN recheck_red base_red
    ON base_red.layer = 'base' AND base_red.view = '' AND base_red.day = w.day AND base_red.rev = b.rev
  LEFT JOIN recheck_red view_red
    ON view_red.layer = 'view' AND view_red.view = $2 AND view_red.day = w.day AND view_red.rev = v.rev
 ORDER BY w.day`;

/**
 * The cache key of a facts read: each day's current view revision and archive
 * status. `$1` day names, `$2` the usage view name, `$3`/`$4` the first and
 * last of those days. A change of either misses the cache. A new read-back on
 * the same revision and status does not. The lookup is a range scan: one
 * index probe per day measured about as slow as the facts statement itself.
 */
export const COLD_ONLY_DAY_CACHE_KEY_SQL = `SELECT d.day::text AS day,
       pv.current_rev::text AS view_rev,
       a.status AS archive_status
  FROM (
    SELECT day FROM cold.partitions
     WHERE layer = 'view' AND dataset = 'usage.logs' AND view = $2
       AND day >= $3::date AND day <= $4::date
    UNION
    SELECT archived_on AS day FROM usage_logs_archive_days
     WHERE archived_on >= $3::date AND archived_on <= $4::date
  ) AS d
  LEFT JOIN cold.partitions pv
    ON pv.layer = 'view' AND pv.dataset = 'usage.logs' AND pv.view = $2 AND pv.day = d.day
  LEFT JOIN usage_logs_archive_days a ON a.archived_on = d.day
 WHERE d.day = ANY($1::date[])`;

function flags(value: unknown): boolean {
  return value === true || value === 't' || value === 'true' || value === 1 || value === '1';
}

function countOrNull(value: unknown): number | null {
  if (typeof value === 'number') return Number.isSafeInteger(value) && value >= 0 ? value : null;
  if (typeof value === 'string' && /^\d+$/u.test(value)) {
    const parsed = Number(value);
    return Number.isSafeInteger(parsed) ? parsed : null;
  }
  return null;
}

function arrayOrEmpty(value: unknown): readonly unknown[] {
  return Array.isArray(value) ? value : [];
}

/** Field ids of a revision's `gaps` (`[{fields: [..], reason}]`). */
function gapFieldIds(value: unknown): readonly number[] {
  const ids: number[] = [];
  for (const gap of arrayOrEmpty(value)) {
    if (typeof gap !== 'object' || gap === null) continue;
    for (const field of arrayOrEmpty((gap as { fields?: unknown }).fields)) {
      const id = countOrNull(field);
      if (id !== null) ids.push(id);
    }
  }
  return ids;
}

function relationNames(value: unknown): readonly string[] {
  return arrayOrEmpty(value).filter((entry): entry is string => typeof entry === 'string');
}

function revision(row: Readonly<Record<string, unknown>>, prefix: 'base' | 'view'): ColdOnlyRevision | null {
  const rev = countOrNull(row[`${prefix}_rev`]);
  const rows = countOrNull(row[`${prefix}_rows`]);
  if (rev === null || rows === null) return null;
  const coverage = row[`${prefix}_coverage`] === 'complete'
    ? 'complete' as const
    : row[`${prefix}_coverage`] === 'partial' ? 'partial' as const : null;
  return {
    rev,
    rows,
    coverage,
    gapFieldIds: gapFieldIds(row[`${prefix}_gaps`]),
    sourceRev: prefix === 'view' ? countOrNull(row['view_source_rev']) : null,
    hasArchiveRelation: prefix === 'base' && relationNames(row['base_relations']).includes('usage_logs_archive'),
    hasHotRelation: prefix === 'base' && relationNames(row['base_relations']).includes('usage_logs'),
  };
}

const AUDIT_ROLES = ['src', 'read', 'spot'] as const;
const SOURCE_MISMATCH_CODES: ReadonlySet<string> = new Set(['source_digest_mismatch', 'source_rows_mismatch']);
/** Reds that checked nothing. They neither anchor a day nor remove an earlier ok. */
const NON_CONCLUSIVE_CODES: ReadonlySet<string> = new Set([
  'unit_failed',
  'unit_missing',
  'unit_setup_failed',
  'unit_version_skew',
]);

interface AuditProblem {
  readonly code: string;
  readonly from: string | null;
  readonly detail: string;
}

function detailRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

function problemOf(value: unknown): AuditProblem | null {
  // The facts statement projects each problem as its code string. A full
  // object still parses, which is what the hand-built fixtures pass.
  if (typeof value === 'string' && value !== '') return { code: value, from: null, detail: '' };
  const record = detailRecord(value);
  if (record === null || typeof record['code'] !== 'string') return null;
  return {
    code: record['code'],
    from: typeof record['from'] === 'string' ? record['from'] : null,
    detail: typeof record['detail'] === 'string' ? record['detail'] : '',
  };
}

function incompleteRoles(detail: Record<string, unknown>): readonly string[] {
  const raw = detail['incomplete_roles'];
  if (!Array.isArray(raw)) return [];
  return AUDIT_ROLES.filter((role) => raw.some((item) => item === role));
}

/** Old `compare_source` text. A `Σ per-org` sum check is not this. */
function legacyCompareSource(problem: AuditProblem): boolean {
  if (problem.from !== null) return false;
  if (!SOURCE_MISMATCH_CODES.has(problem.code)) return false;
  return / org [^:]+: source .+ != export /u.test(problem.detail);
}

/**
 * A red read-back states a data conclusion.
 *
 * Not a conclusion: `unit_failed`, `unit_missing`, `unit_setup_failed`,
 * `unit_version_skew`, and any red whose `incomplete_roles` is non-empty.
 * Those neither anchor the day nor remove an earlier ok.
 *
 * Truncation matches `readAuditRevokes`, and is stricter where the two
 * differ: a present `data_problems_total` greater than zero is a content
 * red. Without that field, `problems_total` above the stored length is a
 * content red unless every stored code is non-conclusive.
 */
export function coldOnlyAuditConcludes(detail: unknown): boolean {
  const record = detailRecord(detail);
  if (record === null) return true;
  if (incompleteRoles(record).length > 0) return false;
  const problems = record['problems'];
  if (!Array.isArray(problems) || problems.length === 0) return true;
  const stored = problems.flatMap((problem) => {
    const parsed = problemOf(problem);
    return parsed === null ? [] : [parsed];
  });
  if (stored.length !== problems.length) return true;
  if (truncatedConclusion(record, stored)) return true;
  const marked = Object.prototype.hasOwnProperty.call(record, 'incomplete_roles');
  const legacyUnit = !marked && stored.some((problem) => problem.code === 'unit_failed');
  return stored.some((problem) => {
    if (NON_CONCLUSIVE_CODES.has(problem.code)) return false;
    if (legacyUnit && legacyCompareSource(problem)) return false;
    return true;
  });
}

/**
 * Whether a short problem list hid a data conclusion. A present
 * `data_problems_total` above zero is conclusive. Without it, overflow is
 * conclusive unless every stored code is non-conclusive.
 */
function truncatedConclusion(detail: Record<string, unknown>, problems: readonly AuditProblem[]): boolean {
  const dataTotal = countOrNull(detail['data_problems_total']);
  if (dataTotal !== null) return dataTotal > 0;
  const total = countOrNull(detail['problems_total']);
  if (total === null || total <= problems.length) return false;
  return problems.some((problem) => !NON_CONCLUSIVE_CODES.has(problem.code));
}

function roleCompleteOk(ok: unknown, detail: unknown): boolean {
  if (!flags(ok)) return false;
  const record = detailRecord(detail);
  return record === null || incompleteRoles(record).length === 0;
}

function auditSortKey(audit: unknown, index: number): { readonly at: string; readonly id: number; readonly index: number } {
  const record = detailRecord(audit);
  const at = record !== null && typeof record['at'] === 'string' ? record['at'] : '';
  const id = record === null ? index : countOrNull(record['id']) ?? index;
  return { at, id, index };
}

/**
 * Whether one revision's read-backs anchor the day.
 *
 * `logsAudits` is `usage_logs`, `archiveAudits` is `usage_logs_archive`, both
 * for the revision being judged (a newer revision does not inherit the
 * previous one's ok; pass only that revision's rows). Each entry is
 * `{ ok, detail, at, id }` and must already be the current
 * `COLD_USAGE_READ_VIEW` (`primary@2`) revision: this function does not
 * filter by view or rev. `archiveStatus` is `usage_logs_archive_days.status`
 * (`null` when the day has no archive row).
 *
 * The authoritative relation is the one that still holds the source. A
 * `cold_ledger` day, including one whose warm row is still `deleting`, is
 * judged on the `usage_logs` read-backs taken before the delete. An archive
 * day whose status is `deleted` or `deleting` ignores those hot read-backs:
 * they are neither an ok nor a red, and only the `usage_logs_archive`
 * timeline is judged. Every other status merges both relations into one
 * timeline ordered by `(at, id)`. `unit_failed`,
 * `unit_missing`, `unit_setup_failed`, `unit_version_skew`, and a red with
 * `incomplete_roles` are skipped. The day is anchored only when the latest
 * conclusion on that timeline is a role-complete ok.
 *
 * The warm-cold-manager scheduler (#3303) calls this same function and must
 * pass `archiveStatus`. A true result means "the latest data conclusion on
 * the authoritative relation of this revision is an ok read-back"; do not
 * narrow or widen that without a new function.
 */
export function coldOnlyDayAnchored(
  logsAudits: readonly unknown[],
  archiveAudits: readonly unknown[],
  archiveStatus: string | null,
  source: UsageSourceKind | null = null,
): boolean {
  const authoritative = source === 'cold_ledger'
    ? logsAudits
    : source === 'archive' || archiveStatus === 'deleted' || archiveStatus === 'deleting'
      ? archiveAudits
      : [...logsAudits, ...archiveAudits];
  const timeline = authoritative.map((audit, index) => ({ audit, ...auditSortKey(audit, index) }));
  timeline.sort((left, right) => {
    if (left.at !== right.at) return left.at < right.at ? -1 : 1;
    if (left.id !== right.id) return left.id - right.id;
    return left.index - right.index;
  });
  let anchored = false;
  for (const { audit } of timeline) {
    const record = detailRecord(audit);
    if (record === null) continue;
    if (roleCompleteOk(record['ok'], record['detail'])) {
      anchored = true;
      continue;
    }
    // A cold-ledger delete empties usage_logs on purpose. That source mismatch
    // is neither an ok nor a red; a real content red still removes the anchor.
    if (!flags(record['ok']) && source === 'cold_ledger' && sourceMismatchOnly(record['detail'])) continue;
    if (!flags(record['ok']) && coldOnlyAuditConcludes(record['detail'])) anchored = false;
  }
  return anchored;
}

/**
 * A red whose every problem is the hot source disappearing. On a cold-ledger
 * day that red is expected after the delete and does not move the anchor.
 */
function sourceMismatchOnly(detail: unknown): boolean {
  const record = detailRecord(detail);
  if (record === null || incompleteRoles(record).length > 0) return false;
  const problems = record['problems'];
  if (!Array.isArray(problems) || problems.length === 0) return false;
  let saw = false;
  for (const problem of problems) {
    const parsed = problemOf(problem);
    if (parsed === null || !SOURCE_MISMATCH_CODES.has(parsed.code)) return false;
    saw = true;
  }
  return saw;
}

/** One relation, in the order given. Prefer {@link coldOnlyDayAnchored} for a day. */
export function coldOnlyRelationAnchored(audits: readonly unknown[]): boolean {
  return coldOnlyDayAnchored(audits, [], null);
}

function auditsOf(value: unknown): readonly unknown[] {
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value) as unknown;
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return Array.isArray(value) ? value : [];
}

/** Parse one statement row. Exported for the tests that build rows by hand. */
export function coldOnlyDayFactsOf(row: Readonly<Record<string, unknown>>): ColdOnlyDayFacts {
  const day = typeof row['day'] === 'string' ? row['day'] : '';
  const status = typeof row['archive_status'] === 'string' ? row['archive_status'] : null;
  const baseRevision = revision(row, 'base');
  const viewRevision = revision(row, 'view');
  const sourceRows = countOrNull(row['source_rows']);
  const archivedRows = countOrNull(row['archived_rows']);
  const deletedRows = countOrNull(row['deleted_rows']);
  const deletedAtSet = flags(row['deleted_at_set']);
  const archiveClosed = status === 'deleted'
    && deletedAtSet
    && sourceRows !== null
    && archivedRows === sourceRows
    && deletedRows === sourceRows;
  const source: UsageSourceKind | null = status === 'deleted' || status === 'deleting' ? 'archive' : null;
  const hotPresent = flags(row['hot_present']);
  const sourceGone = archiveClosed && !hotPresent && coldOnlyRevisionProof(baseRevision, viewRevision,
    countOrNull(row['current_base_rev']), countOrNull(row['current_view_rev']), sourceRows);
  return {
    day,
    archiveStatus: status,
    sourceRows,
    archivedRows,
    deletedRows,
    deletedAtSet,
    baseRevision,
    viewRevision,
    currentBaseRev: countOrNull(row['current_base_rev']),
    currentViewRev: countOrNull(row['current_view_rev']),
    recheckRed: flags(row['base_recheck_red']) || flags(row['view_recheck_red']),
    contentAnchored: coldOnlyDayAnchored(auditsOf(row['logs_audits']), auditsOf(row['archive_audits']), status, source),
    source,
    sourceClosed: archiveClosed,
    hotPresent,
    sourceGone,
  };
}

/** The revision half of the source-gone rule, shared by every source ledger. */
function coldOnlyRevisionProof(
  base: ColdOnlyRevision | null,
  view: ColdOnlyRevision | null,
  currentBaseRev: number | null,
  currentViewRev: number | null,
  expectedRows: number | null,
): boolean {
  if (base === null || view === null || expectedRows === null) return false;
  if (!coldOnlyRevisionLegal(base) || !coldOnlyRevisionLegal(view)) return false;
  if (base.rows !== expectedRows || view.rows !== base.rows) return false;
  if (view.sourceRev === null || view.sourceRev !== base.rev) return false;
  if (currentBaseRev === null || currentBaseRev !== base.rev) return false;
  if (currentViewRev === null || currentViewRev !== view.rev) return false;
  return true;
}

/** A ledger row merged onto a day whose revision facts are already loaded. */
function withUsageSourceDay(facts: ColdOnlyDayFacts, row: Readonly<Record<string, unknown>>, sourceDay: UsageSourceDay): ColdOnlyDayFacts {
  const status = sourceDay.status;
  const source = sourceDay.source;
  const archiveClosed = facts.archiveStatus === 'deleted'
    && facts.deletedAtSet
    && facts.sourceRows !== null
    && facts.archivedRows === facts.sourceRows
    && facts.deletedRows === facts.sourceRows;
  const ledgerClosed = source === 'cold_ledger'
    && status === 'deleted'
    && sourceDay.deletedRows !== null
    && facts.baseRevision !== null
    && sourceDay.deletedRows === facts.baseRevision.rows;
  const expectedRows = source === 'cold_ledger' ? facts.baseRevision?.rows ?? null : facts.sourceRows;
  const sourceGone = (archiveClosed || ledgerClosed)
    && !facts.hotPresent
    && coldOnlyRevisionProof(facts.baseRevision, facts.viewRevision,
      facts.currentBaseRev, facts.currentViewRev, expectedRows);
  return {
    ...facts,
    source,
    sourceClosed: archiveClosed || ledgerClosed,
    sourceGone,
    sourceRows: source === 'cold_ledger' ? sourceDay.sourceRows : facts.sourceRows,
    deletedRows: source === 'cold_ledger' ? sourceDay.deletedRows : facts.deletedRows,
    contentAnchored: coldOnlyDayAnchored(auditsOf(row['logs_audits']), auditsOf(row['archive_audits']), facts.archiveStatus, source),
  };
}

const FACTS_CACHE_TTL_MS = 60_000;
const FACTS_CACHE_MAX = 32;
let factsCacheGeneration = 0;
const factsCache = new WeakMap<object, Map<string, { readonly generation: number; readonly at: number; readonly facts: Map<string, ColdOnlyDayFacts> }>>();

/** Drop the process-local facts cache. Tests that change a revision's read-backs call this. */
export function resetColdOnlyDayFactsCache(): void {
  factsCacheGeneration += 1;
}

/**
 * The lifecycle status of `days`, from the archive day table and the cold
 * ledger. A missing `cold.usage_hot_delete_days` is an absent fact, not an
 * error: the archive-only statement runs instead and every day it does not name
 * stays "source still expected". Any read failure is also fail closed (an empty
 * map), because this answer lets a reader stop asking for the source.
 */
function sourceDayStatement(mode: UsageSourceReadMode, ledger: boolean, archive: boolean): string {
  if (mode === 'main') {
    if (ledger && archive) return USAGE_SOURCE_DAY_SQL_MAIN;
    return ledger ? USAGE_SOURCE_DAY_LEDGER_SQL_MAIN : USAGE_SOURCE_DAY_NO_LEDGER_SQL_MAIN;
  }
  if (ledger && archive) return USAGE_SOURCE_DAY_SQL;
  return ledger ? USAGE_SOURCE_DAY_LEDGER_SQL : USAGE_SOURCE_DAY_NO_LEDGER_SQL;
}

async function readUsageSourceDays(
  db: ColdOnlyQuery,
  unique: readonly string[],
  mode: UsageSourceReadMode,
): Promise<Map<string, UsageSourceDay>> {
  try {
    const probe = await db.query(USAGE_SOURCE_DAY_PROBE_SQL);
    const ledger = typeof probe.rows[0]?.['rel'] === 'string';
    const archiveProbe = await db.query(USAGE_SOURCE_DAY_ARCHIVE_PROBE_SQL);
    const archive = typeof archiveProbe.rows[0]?.['rel'] === 'string';
    if (!ledger && !archive) return new Map();
    const rows = await db.query(sourceDayStatement(mode, ledger, archive), [unique]);
    const parsedRows = rows.rows.flatMap((row) => {
      const day = typeof row['day'] === 'string' ? row['day'] : '';
      if (day === '') return [];
      return [{
        day,
        archiveStatus: typeof row['archive_status'] === 'string' ? row['archive_status'] : null,
        ledgerStatus: typeof row['ledger_status'] === 'string' ? row['ledger_status'] : null,
        archiveDeleted: countOrNull(row['archive_deleted_rows']),
        archiveSource: countOrNull(row['archive_source_rows']),
        ledgerDeleted: countOrNull(row['ledger_deleted_rows']),
        ledgerSource: countOrNull(row['ledger_source_rows']),
      }];
    });
    const out = new Map<string, UsageSourceDay>();
    for (const parsed of parsedRows) {
      const { day, archiveStatus, ledgerStatus, archiveDeleted, archiveSource, ledgerDeleted, ledgerSource } = parsed;
      const archiveTerminal = archiveStatus === 'deleted' || archiveStatus === 'deleting';
      // `main` keeps the pre-change rule (any deleting row is a source).
      // Cold evidence hides a not-yet-started delete, including the grace
      // barrier: nothing has been removed, so the hot table is still complete.
      const ledgerTerminal = mode === 'main'
        ? ledgerStatus === 'deleted' || ledgerStatus === 'deleting'
        : ledgerStatus === 'deleted' || (ledgerStatus === 'deleting' && (ledgerDeleted ?? 0) > 0);
      const archiveClosedNow = archiveStatus === 'deleted' && archiveSource !== null && archiveDeleted === archiveSource;
      // A closed archive day keeps the warm proof. A cold-ledger `deleted` day
      // is not hidden by an open archive status (`copying`, `deleting`).
      const source: UsageSourceKind | null = archiveClosedNow
        ? 'archive'
        : ledgerStatus === 'deleted'
          ? 'cold_ledger'
          : archiveTerminal
            ? 'archive'
            : ledgerTerminal ? 'cold_ledger' : null;
      const status = source === 'archive'
        ? archiveStatus as 'deleted' | 'deleting'
        : source === 'cold_ledger' ? ledgerStatus as 'deleted' | 'deleting' : null;
      const closed = source === 'archive'
        ? archiveClosedNow
        : source === 'cold_ledger' && status === 'deleted' && ledgerDeleted !== null;
      out.set(day, {
        day,
        source,
        status,
        deletedRows: source === 'cold_ledger' ? ledgerDeleted : archiveDeleted,
        sourceRows: source === 'cold_ledger' ? ledgerSource : archiveSource,
        closed,
      });
    }
    return out;
  } catch (error) {
    // A missing ledger table is an absent fact, not a blind reader: the
    // archive-only/ledger row is simply "not source-gone". Any other error is
    // also fail closed; callers that need to tell blindness apart use the
    // strict source-gone statement directly.
    if (typeof error === 'object' && error !== null) {
      const record = error as { readonly code?: unknown; readonly sqlState?: unknown };
      if (record.code === '42P01' || record.sqlState === '42P01') return new Map();
    }
    throw error;
  }
}

/** Strict form for a caller that already has its own fail-closed path. */
export async function usageSourceDaysStrict(
  db: ColdOnlyQuery,
  days: readonly string[],
  mode: UsageSourceReadMode = 'main',
): Promise<Map<string, UsageSourceDay>> {
  if (days.length === 0) return new Map();
  return readUsageSourceDays(db, [...new Set(days)].sort(), mode);
}

/**
 * Default (`main`) swallows a read error into an empty map, the pre-change
 * fail-open. `cold-evidence` rethrows: a ledger error must not be answered
 * as a hot day (that is a short 200 while a delete is in progress).
 */
export async function usageSourceDays(
  db: ColdOnlyQuery,
  days: readonly string[],
  mode: UsageSourceReadMode = 'main',
): Promise<Map<string, UsageSourceDay>> {
  try {
    return await usageSourceDaysStrict(db, days, mode);
  } catch (error) {
    if (mode === 'cold-evidence') throw error;
    return new Map();
  }
}

/**
 * The days of `days` that {@link usageSourceGonePredicate} accepts.
 * Fail closed: a missing ledger table uses the archive-only statement, and
 * any read error returns an empty set, never a throw.
 */
export async function usageSourceGoneDays(db: ColdOnlyQuery, days: readonly string[]): Promise<Set<string>> {
  if (days.length === 0) return new Set();
  const unique = [...new Set(days)].sort();
  try {
    const probe = await db.query(USAGE_SOURCE_DAY_PROBE_SQL);
    const ledger = typeof probe.rows[0]?.['rel'] === 'string';
    const rows = await db.query(ledger ? USAGE_SOURCE_GONE_SQL : USAGE_SOURCE_GONE_NO_LEDGER_SQL,
      [unique, COLD_USAGE_READ_VIEW]);
    return new Set(rows.rows.flatMap((row) => (typeof row['day'] === 'string' ? [row['day']] : [])));
  } catch {
    return new Set();
  }
}

function factsCacheKey(rows: readonly Record<string, unknown>[], days: readonly string[]): string {
  const byDay = new Map<string, { readonly rev: string; readonly status: string }>();
  for (const row of rows) {
    const day = typeof row['day'] === 'string' ? row['day'] : '';
    if (day === '') continue;
    byDay.set(day, {
      rev: typeof row['view_rev'] === 'string' ? row['view_rev'] : '',
      status: typeof row['archive_status'] === 'string' ? row['archive_status'] : '',
    });
  }
  return days.map((day) => {
    const found = byDay.get(day);
    return `${day}|${found?.rev ?? ''}|${found?.status ?? ''}`;
  }).join(',');
}

/**
 * One statement for any number of days: the facts of each, missing facts as nulls.
 *
 * A hit is reused for 60 seconds when the day set, each day's current view
 * revision, archive status, and cold-ledger source are unchanged. The source
 * key includes the ledger source, status, and deleted_rows, so a barrier mark
 * or a committed batch misses. A content red on that same revision can stay
 * invisible for at most a minute. An in-progress delete is not served from
 * the cold snapshot; the reader refuses it.
 */
export async function coldOnlyDayFacts(
  db: ColdOnlyQuery,
  days: readonly string[],
  view: string = COLD_USAGE_READ_VIEW,
  mode: UsageSourceReadMode = 'main',
): Promise<Map<string, ColdOnlyDayFacts>> {
  if (days.length === 0) return new Map();
  const unique = [...new Set(days)].sort();
  const sourceDays = await usageSourceDays(db, unique, mode);
  const sourceKey = unique.map((day) => {
    const found = sourceDays.get(day);
    return `${found?.source ?? ''}:${found?.status ?? ''}:${found?.deletedRows ?? ''}`;
  }).join(',');
  let cacheKey = '';
  try {
    const keyRows = await db.query(COLD_ONLY_DAY_CACHE_KEY_SQL, [unique, view, unique[0], unique[unique.length - 1]]);
    cacheKey = `${mode}\n${view}\n${sourceKey}\n${factsCacheKey(keyRows.rows, unique)}`;
    const hit = factsCache.get(db)?.get(cacheKey);
    if (hit !== undefined && hit.generation === factsCacheGeneration && Date.now() - hit.at < FACTS_CACHE_TTL_MS) {
      return hit.facts;
    }
  } catch {
    cacheKey = '';
  }
  const rows = await db.query(COLD_ONLY_DAY_FACTS_SQL, [unique, view, unique[0], unique[unique.length - 1]]);
  const out = new Map<string, ColdOnlyDayFacts>();
  for (const row of rows.rows) {
    const parsed = coldOnlyDayFactsOf(row);
    if (parsed.day === '') continue;
    const sourceDay = sourceDays.get(parsed.day);
    const merged = sourceDay === undefined ? parsed : withUsageSourceDay(parsed, row, sourceDay);
    out.set(parsed.day, merged);
  }
  if (cacheKey !== '') {
    let bucket = factsCache.get(db);
    if (bucket === undefined) {
      bucket = new Map();
      factsCache.set(db, bucket);
    }
    if (bucket.size >= FACTS_CACHE_MAX) {
      const oldest = bucket.keys().next().value;
      if (oldest !== undefined) bucket.delete(oldest);
    }
    bucket.set(cacheKey, { generation: factsCacheGeneration, at: Date.now(), facts: out });
  }
  return out;
}

/**
 * The lifecycle ledger finished with this day: `deleted` and its row
 * accounting closes, from the archive day table or the cold hot-delete ledger,
 * and no hot row is left in the day. A late row after the delete keeps the
 * day out: the same rule's SQL form requires an empty `usage_logs` window.
 * This is the classification ("do not fall back to a hot clearance"), not the
 * served proof; {@link coldOnlyDayServed} also requires the revisions.
 */
export function coldOnlySourceDeleted(facts: ColdOnlyDayFacts): boolean {
  return facts.sourceClosed && !facts.hotPresent;
}

/**
 * A legal full-row manifest: nothing missing, or only the sixteen retired
 * scalars (`coverage` `partial` with an H0-only gap, the archived shape the
 * cold line already serves). A gap of any other field leaves the day out.
 */
export function coldOnlyRevisionLegal(revision: ColdOnlyRevision | null): boolean {
  if (revision === null || revision.coverage === null) return false;
  if (revision.coverage === 'complete') return revision.gapFieldIds.length === 0;
  return revision.gapFieldIds.length > 0 && usageLogsH0RetiredGapsOnly(revision.gapFieldIds);
}

/**
 * A content anchor for the current revisions. A red recheck refuses. The
 * anchor itself is a role-complete ok read-back that a later content red has
 * not replaced. No such read-back refuses.
 */
export function coldOnlyContentAnchored(facts: ColdOnlyDayFacts): boolean {
  if (facts.recheckRed) return false;
  return facts.contentAnchored;
}

/** What the reader actually served, when the caller has a manifest to pin the proof to. */
export interface ColdOnlyServedRevisions {
  readonly baseRev: number | null;
  readonly viewRev: number | null;
}

/**
 * May this day be answered from its cold revision? Every clause is required and
 * every absent fact refuses; see the module comment for why each one is there.
 * `served` pins the admitted revisions to the ones the reader resolved, so a
 * revision published between the two statements cannot be admitted on a stale
 * manifest.
 */
export function coldOnlyDayServed(facts: ColdOnlyDayFacts, served?: ColdOnlyServedRevisions): boolean {
  if (facts.hotPresent) return false;
  if (!facts.sourceGone) return false;
  const base = facts.baseRevision;
  const view = facts.viewRevision;
  if (base === null || view === null) return false;
  if (!coldOnlyRevisionLegal(base) || !coldOnlyRevisionLegal(view)) return false;
  if (facts.source === 'archive') {
    if (!base.hasArchiveRelation) return false;
    if (facts.archivedRows === null || facts.sourceRows !== facts.archivedRows) return false;
    if (base.rows !== facts.archivedRows) return false;
  } else if (facts.source === 'cold_ledger') {
    if (!base.hasHotRelation) return false;
    if (facts.deletedRows === null || facts.deletedRows !== base.rows) return false;
  } else {
    return false;
  }
  if (view.rows !== base.rows) return false;
  if (view.sourceRev === null || view.sourceRev !== base.rev) return false;
  if (facts.currentBaseRev === null || facts.currentBaseRev !== base.rev) return false;
  if (facts.currentViewRev === null || facts.currentViewRev !== view.rev) return false;
  if (served !== undefined && (served.baseRev !== base.rev || served.viewRev !== view.rev)) return false;
  return coldOnlyContentAnchored(facts);
}
